import React, { FC, useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { endOfDay, startOfDay } from 'date-fns'

import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import { TableComponent } from '../../components/Table'
import { columns, IChartData } from './tradingList'
import { useRest } from '../../components/Context'
import { Loader } from '../../components/Loader'
import { ChartArea } from '../../components/Chart'
import useDebounce from '../../hooks/useDebounce'
import { DataPicker } from '../../components/DatePicker'
import { IEnums } from '../../components/InputSelect'
import { FilterSelect } from '../../components/FilterSelect'
import { CRM_TRADING_STATUS } from '../../constants/CrmTradingStatus'
import { CRM_TRADING_TYPE } from '../../constants/CrmTradingType'
import { Search } from '../../components/Search'
import { generateParams } from '../../utils'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(2),
    '& p': {
      display: 'none',
    },
  },
  topMrg: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(6),
  },
  title: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  txtError: {
    fontSize: 16,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  search: {
    paddingRight: 0,
  },
  rightSide: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
  },
}))

export const Trading: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { rxios } = useRest()
  const { search } = useLocation()

  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  // eslint-disable-next-line
  const [valuesChart, setValuesChart] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingTable, setLoadingTable] = useState<boolean>(false)
  const [startDateSelected, setStartDateSelected] = useState<string>('')
  const [endDateSelected, setEndDateSelected] = useState<string>('')
  const [transactionStatus, setTransactionStatus] = useState<string>('')
  const [transactionType, setTransactionType] = useState<string>('')
  const [pair, setPair] = useState<string>('')
  const [pairsSelect, setPairsSelect] = useState<IEnums[]>([])
  const [queryString, setQueryString] = useState<string>('')
  const [searchDebounce, setSearchDebounce] = useState<string>('')
  const debouncedValue = useDebounce<string>(queryString, 200)
  const [isInitDate, setInitDate] = useState<boolean>(false)
  const [lastDate, setLastDate] = useState<string>()
  const [firstDate, setFirstDate] = useState<string>()

  const formMethods = useForm({
    mode: 'onBlur',
  })
  const { watch, getValues } = formMethods

  const stringParams = new URLSearchParams(search)
  const pageUrl: number | string | null = stringParams.get('page')
  // eslint-disable-next-line
  useEffect(() => setSearchDebounce(queryString), [debouncedValue])

  useEffect(() => {
    const date = new Date()
    setLastDate(
      date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    )
    setFirstDate(
      new Date(date.setDate(date.getDate() - 30)).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    )
  }, [setLastDate, setFirstDate])

  const pushParamsToURL = useCallback(
    (startDate, endDate, status, type, pairs, searchString) => {
      const currentUrlParams = new URLSearchParams(window.location.search)

      if (startDate) {
        currentUrlParams.set('startDate', startDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('startDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (endDate) {
        currentUrlParams.set('endDate', endDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('endDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (status) {
        currentUrlParams.set('status', status)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('status')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (pairs) {
        currentUrlParams.set('pair', pairs.toUpperCase())
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('pair')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (type) {
        currentUrlParams.set('type', type)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('type')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (searchString) {
        currentUrlParams.set('search', searchString)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('search')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }
    },
    [history],
  )

  const loadData = useCallback(
    async (page, startDate = '', endDate = '', status = '', type = '', pairs = '', searchString = '') => {
      let cleanupFunction = false
      setLoadingTable(true)

      rxios
        .get(
          `trading/trades?${generateParams({
            page,
            count: '10',
            startDate,
            endDate,
            status,
            type,
            pair: pairs.toUpperCase(),
            search: searchString.toUpperCase(),
          })}`,
        )
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            history.push(`trading?page=${page}`)
            pushParamsToURL(startDate, endDate, status, type, pairs, searchString)

            if (!cleanupFunction) setLoadingTable(false)
          },
          (error) => {
            pushParamsToURL(startDate, endDate, status, type, pairs, searchString)

            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoadingTable(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [pageUrl],
  )

  const initData = useCallback(
    async (page) => {
      let cleanupFunction = false
      setLoading(true)

      rxios.get(`trading/trades?page=${page}&count=10`).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setValues(next)
          if (!cleanupFunction) setLoading(false)
          setInitDate(true)

          history.push(`trading?page=${page}`)
        },
        (error) => {
          toast.error(error)
          setLoading(false)
        },
        () => {
          setLoading(false)
        },
      )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [rxios],
  )

  const loadChartData = useCallback(async () => {
    let cleanupFunction = false
    setLoading(true)

    rxios.get('trading/last-month-trades').subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setValuesChart(next)
        // eslint-disable-next-line
        if (!cleanupFunction) setLoading(false)
      },
      (error) => {
        toast.error(error)
        setLoading(false)
      },
      () => {
        setLoading(false)
      },
    )
    cleanupFunction = true
    // eslint-disable-next-line
  }, [])

  const loadPairsList = useCallback(async () => {
    setLoading(true)

    rxios.get(`trading/pairs`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        const pairs = []

        // eslint-disable-next-line no-restricted-syntax
        for (const name of next) {
          pairs.push({
            value: name,
            label: name,
          })
        }
        setPairsSelect(pairs)
      },
      (error) => {
        toast.error(error)
      },
    )
    // eslint-disable-next-line
  }, [rxios])

  useEffect(() => {
    ;(async () => {
      await loadChartData()
      await initData(pageUrl || '1')
      await loadPairsList()
    })()
    // eslint-disable-next-line
  }, [])

  const correctDate = valuesChart.map((item: IChartData) =>
    item.timestamp
      ? {
          ...item,
          timestamp: new Date(item.timestamp).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }),
        }
      : item,
  )

  const newValuesChart = correctDate.map((item: IChartData) => (item.sell ? { ...item, sell: -item.sell } : item))

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'startDate') setStartDateSelected(value?.startDate?.toISOString())
      if (name === 'endDate') setEndDateSelected(value?.endDate?.toISOString())
      if (name === 'transactionStatus')
        setTransactionStatus(getValues().transactionStatus.toLowerCase().replaceAll(' ', '_'))
      if (name === 'transactionType') setTransactionType(getValues().transactionType.toLowerCase())
      if (name === 'pair') setPair(value?.pair.toLowerCase().replaceAll(' ', '_'))
      if (name === 'search') setQueryString(value?.[name])
    })
    return () => subscription.unsubscribe()
  }, [watch, getValues])

  useEffect(() => {
    if (isInitDate) {
      setEndDateSelected(endDateSelected === '1970-01-01' ? '' : endDateSelected)
      ;(async () => {
        await loadData(
          1,
          startDateSelected || '',
          endDateSelected || '',
          transactionStatus || '',
          transactionType || '',
          pair || '',
          searchDebounce || '',
        )
      })()
    }
    // eslint-disable-next-line
  }, [startDateSelected, endDateSelected, transactionStatus, transactionType, pair, searchDebounce])

  return (
    <Grid container spacing={3} className={classes.topMrg}>
      <Grid item xs={12}>
        <Typography variant={'h1'}>Trading</Typography>
      </Grid>

      {loading ? (
        <Loader />
      ) : (
        <>
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.title}>
              {newValuesChart.length} Trade orders, {firstDate} – {lastDate}
            </Typography>

            {valuesChart.length ? (
              <ChartArea dataChart={newValuesChart} />
            ) : (
              <Typography variant="subtitle1" className={classes.txtError}>
                Sorry, No Trade Orders, {firstDate} – {lastDate}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Grid container className={classes.topWrap}>
              <FormProvider {...formMethods}>
                <Grid container className={classes.rightSide}>
                  <DataPicker
                    placeholder={'Start Date'}
                    margin="8px 8px 8px 0"
                    dataPickerName={'startDate'}
                    required={false}
                    maxDate={getValues().endDate}
                    formatValue={startOfDay}
                  />
                  <DataPicker
                    placeholder={'End Date'}
                    margin={8}
                    dataPickerName={'endDate'}
                    required={false}
                    minDate={getValues().startDate}
                    formatValue={endOfDay}
                  />
                  <FilterSelect
                    inputName={'transactionType'}
                    placeholder="Type"
                    enums={CRM_TRADING_TYPE}
                    width={138}
                    ml={8}
                    mr={8}
                  />
                  <FilterSelect
                    inputName={'transactionStatus'}
                    placeholder="Status"
                    enums={CRM_TRADING_STATUS}
                    width={138}
                    ml={8}
                    mr={8}
                  />
                  <FilterSelect inputName={'pair'} placeholder="Pair" width={138} ml={8} mr={8} enums={pairsSelect} />
                </Grid>
                <Search pr={16} pl={0} inputName={'search'} className={classes.search} />
              </FormProvider>
            </Grid>

            <TableComponent
              columns={columns}
              data={values.items}
              params={values.meta}
              loading={loadingTable}
              loadData={(pageNumber: number | string | null) =>
                loadData(
                  pageNumber,
                  startDateSelected || '',
                  endDateSelected || '',
                  transactionStatus || '',
                  transactionType || '',
                  pair || '',
                  searchDebounce || '',
                )
              }
            />
          </Grid>
        </>
      )}
    </Grid>
  )
}
